import { useState } from "react";
import { useAuthContext } from "@/contexts/AuthContext";
import { useNotificationPreferences } from "@/hooks/useNotifications";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { LogOut, Bell, Heart, MessageCircle, Shield, MapPin, Globe, Star, ArrowLeft, Zap } from "lucide-react";
import PushNotificationToggle from "@/components/settings/PushNotificationToggle";
import LocationButton from "@/components/profile/LocationButton";
import VerificationModal from "@/components/profile/VerificationModal";

const SettingsPage = () => {
  const { user, profile, signOut, refreshProfile } = useAuthContext();
  const { preferences, updatePreferences } = useNotificationPreferences();
  const navigate = useNavigate();
  const [showVerification, setShowVerification] = useState(false);

  const handleSignOut = async () => {
    try {
      const { error } = await signOut();
      if (error) {
        console.error("Sign out error:", error);
        toast({ 
          title: "Error signing out", 
          description: error.message || "Please try again",
          variant: "destructive" 
        });
      } else {
        // Navigate to entry page after successful sign out
        // The auth state change listener will handle clearing state
        navigate("/entry", { replace: true });
      }
    } catch (err) {
      console.error("Sign out exception:", err);
      toast({ 
        title: "Error signing out", 
        description: "An unexpected error occurred",
        variant: "destructive" 
      });
    }
  };

  const togglePref = async (key: string, value: boolean) => {
    await updatePreferences({ [key]: value });
  };

  return (
    <div className="min-h-screen sky-gradient pb-24">
      <header className="sticky top-0 bg-card/95 backdrop-blur-lg border-b border-border z-30 px-4 py-4">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 -ml-2 rounded-full hover:bg-muted/50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-center">Settings</h1>
          <div className="w-9" />
        </div>
      </header>

      <div className="p-4 max-w-lg mx-auto space-y-4">
        {/* Push Notifications */}
        <div className="bg-card rounded-2xl p-4">
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5 text-pop" /> Push Notifications
          </h2>
          <PushNotificationToggle />
        </div>

        {/* Location & Verification */}
        <div className="bg-card rounded-2xl p-4 space-y-4">
          <h2 className="font-semibold flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" /> Location & Verification
          </h2>
          <div className="flex flex-col gap-3">
            <LocationButton 
              hasLocation={!!(profile?.latitude && profile?.longitude)} 
              onLocationUpdated={refreshProfile}
            />
            <button
              onClick={() => setShowVerification(true)}
              className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium transition ${
                profile?.is_verified 
                  ? "bg-primary/10 text-primary" 
                  : "bg-primary text-primary-foreground"
              }`}
            >
              <Shield className="w-4 h-4" />
              {profile?.is_verified ? "✓ Verified" : "Get Verified"}
            </button>
          </div>
        </div>

        {/* Notification Preferences */}
        <div className="bg-card rounded-2xl p-4">
          <h2 className="font-semibold mb-4">Notification Types</h2>
          <div className="space-y-3">
            {[
              { key: "likes_enabled", label: "Likes", icon: Heart },
              { key: "pops_enabled", label: "Pops", icon: Bell },
              { key: "matches_enabled", label: "Matches", icon: Heart },
              { key: "messages_enabled", label: "Messages", icon: MessageCircle },
              { key: "location_joins_enabled", label: "Location Joins", icon: Globe },
              { key: "favorites_enabled", label: "Favorite Booth Alerts", icon: Star },
              { key: "portal_enabled", label: "Portal Activity", icon: Zap },
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between py-2">
                <span className="flex items-center gap-2">
                  <item.icon className="w-4 h-4 text-muted-foreground" />
                  {item.label}
                </span>
                <button
                  onClick={() => togglePref(item.key, !((preferences as any)?.[item.key] !== false))}
                  className={`w-12 h-7 rounded-full transition-colors ${(preferences as any)?.[item.key] !== false ? "bg-pop" : "bg-muted"}`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${(preferences as any)?.[item.key] !== false ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card rounded-2xl p-4">
          <p className="text-sm text-muted-foreground mb-2">Signed in as</p>
          <p className="font-medium">{user?.email}</p>
        </div>

        <button onClick={handleSignOut} className="w-full py-4 rounded-xl bg-destructive text-destructive-foreground font-semibold flex items-center justify-center gap-2">
          <LogOut className="w-5 h-5" /> Sign Out
        </button>
      </div>

      <VerificationModal
        isOpen={showVerification}
        onClose={() => setShowVerification(false)}
        onSuccess={() => refreshProfile()}
      />
    </div>
  );
};

export default SettingsPage;
